import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const HoardingShowcase = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const hoardingImages = [
    {
      url: "https://customer-assets.emergentagent.com/job_insmart-media/artifacts/l2rzf4d7_2c302c_72a4bf5edf6a49b592836867f9abbaaa~mv2.avif",
      title: "Real Estate Hoarding Campaign",
      description: "Premium billboard advertising for property developments"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_insmart-media/artifacts/0mqgivv7_b47d2338178613.575812a26c485.jpg",
      title: "Product Billboard Advertising",
      description: "High-impact hoarding for FMCG brands"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_insmart-media/artifacts/pg20ivcp_hoarding-advertising-services.jpg",
      title: "Beverage Hoarding Campaign",
      description: "Vibrant outdoor advertising for consumer brands"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_insmart-media/artifacts/nz8jgfod_real-estate-hoarding-design-scaled-e1701161223931-2048x1172.webp",
      title: "Construction Hoarding Design",
      description: "Large-scale billboard advertising for real estate projects"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_insmart-media/artifacts/v5mczyc2_hoarding-design-1000x1000.jpg",
      title: "Creative Hoarding Solutions",
      description: "Professional billboard design and installation services"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_insmart-media/artifacts/yj6g8d7a_Gemini_Generated_Image_fhbqqofhbqqofhbq.png",
      title: "Street Hoarding Campaign - Our Work",
      description: "IN SMART MEDIA executed campaigns across Solapur"
    }
  ];

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % hoardingImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [hoardingImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % hoardingImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + hoardingImages.length) % hoardingImages.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <section id="hoarding-advertising" className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Hoarding Advertising <span className="text-yellow-400">in Solapur</span>
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Solapur's leading hoarding advertising experts - Strategic placements for maximum brand visibility
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Main Carousel */}
          <div className="relative h-96 md:h-[500px] rounded-2xl overflow-hidden shadow-2xl">
            {hoardingImages.map((image, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                  index === currentSlide
                    ? 'opacity-100 scale-100'
                    : 'opacity-0 scale-95'
                }`}
              >
                <img
                  src={image.url}
                  alt={image.title}
                  width="800"
                  height="500"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
                {/* Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                
                {/* Text Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-3xl md:text-4xl font-bold mb-2">{image.title}</h3>
                  <p className="text-lg text-gray-200">{image.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all hover:scale-110 z-20"
            aria-label="Previous slide"
          >
            <ChevronLeft size={32} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all hover:scale-110 z-20"
            aria-label="Next slide"
          >
            <ChevronRight size={32} />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {hoardingImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentSlide
                    ? 'bg-yellow-400 w-12 h-3'
                    : 'bg-white/40 hover:bg-white/60 w-3 h-3'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Key Benefits */}
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all">
            <div className="text-4xl font-bold text-yellow-400 mb-2">100+</div>
            <div className="text-white font-semibold">Prime Locations</div>
            <div className="text-blue-200 text-sm mt-2">Strategic hoarding placements across Solapur</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all">
            <div className="text-4xl font-bold text-yellow-400 mb-2">24/7</div>
            <div className="text-white font-semibold">Brand Visibility</div>
            <div className="text-blue-200 text-sm mt-2">Round-the-clock exposure to thousands daily</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all">
            <div className="text-4xl font-bold text-yellow-400 mb-2">300+</div>
            <div className="text-white font-semibold">Successful Campaigns</div>
            <div className="text-blue-200 text-sm mt-2">Proven track record in hoarding advertising</div>
          </div>
        </div>
      </div>
    </section>
  );
};
